import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Loader2,
  Save,
  AlertCircle,
  Upload,
  Link as LinkIcon,
  Trash2,
  Eye,
  EyeOff,
  Monitor,
  Smartphone,
  RefreshCw,
  GripVertical,
  History,
  RotateCcw,
  X,
  ChevronDown,
} from "lucide-react";
import { authHeaders } from "@/lib/adminAuth";
import { TOK_PHILOSOPHY } from "@/lib/philosophy";
import {
  MANAGED_SECTIONS,
  resolveOrder,
  isVisible,
} from "@/lib/homepageSections";
import { PAGE_SCHEMAS, PAGE_SCHEMA_BY_KEY } from "@/lib/cmsSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Object helpers --------------------------------------------------

function getByPath(obj, path) {
  return path.split(".").reduce((node, p) => (node == null ? undefined : node[p]), obj);
}

function setByPath(obj, path, value) {
  const parts = path.split(".");
  const next = { ...obj };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i];
    cursor[p] = cursor[p] && typeof cursor[p] === "object" ? { ...cursor[p] } : {};
    cursor = cursor[p];
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

// ---------- Main editor ----------------------------------------------------

export default function ContentEditor({ initialPage, initialGroup } = {}) {
  const [page, setPage] = useState(() =>
    initialPage && PAGE_SCHEMA_BY_KEY[initialPage] ? initialPage : "homepage"
  );
  const schema = PAGE_SCHEMA_BY_KEY[page] || PAGE_SCHEMAS[0];
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [device, setDevice] = useState("desktop"); // desktop | mobile
  const [desktopWidth, setDesktopWidth] = useState(1440);
  const [previewKey, setPreviewKey] = useState(0); // force iframe refresh
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snapshotPreview, setSnapshotPreview] = useState(null);

  const load = async (targetPage) => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/content/${targetPage}`,
        { headers: authHeaders() }
      );
      setDoc(r.data);
      setDraft(r.data.draft || {});
    } catch (e) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // Reset transient UI state when switching pages
    setHistoryOpen(false);
    setSnapshotPreview(null);
  }, [page]);

  // Scroll to the requested section group once the editor has loaded.
  useEffect(() => {
    if (!initialGroup || loading) return;
    const el = document.querySelector(
      `[data-testid="content-group-${initialGroup}"]`
    );
    if (el) {
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-1", "ring-amber-400/60");
        setTimeout(() => el.classList.remove("ring-1", "ring-amber-400/60"), 2200);
      }, 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialGroup, loading, page]);

  const update = (key, value) => setDraft((d) => setByPath(d, key, value));

  const saveDraft = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/content/${page}/draft`, draft, {
        headers: authHeaders(),
      });
      setDoc(r.data);
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!window.confirm(`Publish these changes to the live ${schema.label}?`)) return;
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/content/${page}/draft`, draft, { headers: authHeaders() });
      const r = await axios.post(
        `${API}/admin/content/${page}/publish`,
        {},
        { headers: authHeaders() }
      );
      setDoc(r.data);
      setPreviewKey((k) => k + 1);
      toast.success("Published — live site is updated");
    } catch (e) {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard your unpublished changes? This cannot be undone.")) return;
    try {
      const r = await axios.post(
        `${API}/admin/content/${page}/discard`,
        {},
        { headers: authHeaders() }
      );
      setDoc(r.data);
      setDraft(r.data.draft || {});
      toast.success("Draft discarded");
    } catch (e) {
      toast.error("Discard failed");
    }
  };

  const previewSnapshot = async (snapshotId) => {
    try {
      const r = await axios.get(
        `${API}/admin/content/${page}/snapshot/${snapshotId}`,
        { headers: authHeaders() }
      );
      setSnapshotPreview(r.data);
    } catch (e) {
      toast.error("Could not load snapshot");
    }
  };

  const restoreSnapshot = async (snapshotId) => {
    if (
      !window.confirm(
        "Restore this version? Your current published version will be saved into history first, so you can roll back the restore."
      )
    )
      return;
    try {
      const r = await axios.post(
        `${API}/admin/content/${page}/restore/${snapshotId}`,
        {},
        { headers: authHeaders() }
      );
      setDoc(r.data);
      setDraft(r.data.draft || {});
      setPreviewKey((k) => k + 1);
      setSnapshotPreview(null);
      const summary = r.data?.restore_summary || "Live site reverted";
      toast.success(`Restored — ${summary}`);
    } catch (e) {
      toast.error("Restore failed");
    }
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center gap-3 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin text-amber-400" /> Loading…
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(doc?.draft || {});
  const hasPendingDraft = doc?.has_pending_draft || dirty;

  return (
    <div data-testid="admin-content-editor" className="space-y-6">
      <PageSelector
        page={page}
        onChange={setPage}
        schemas={PAGE_SCHEMAS}
      />

      <StatusBar
        pageLabel={schema.label}
        hasPendingDraft={hasPendingDraft}
        publishedAt={doc?.published_at}
        publishedBy={doc?.published_by}
        saving={saving}
        publishing={publishing}
        dirty={dirty}
        snapshotCount={doc?.snapshots?.length || 0}
        onSave={() => saveDraft()}
        onPublish={publish}
        onDiscard={discard}
        onToggleHistory={() => setHistoryOpen((v) => !v)}
        historyOpen={historyOpen}
      />

      {historyOpen && (
        <SnapshotHistory
          snapshots={doc?.snapshots || []}
          onPreview={previewSnapshot}
          onRestore={restoreSnapshot}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {snapshotPreview && (
        <SnapshotPreviewModal
          snapshot={snapshotPreview}
          onClose={() => setSnapshotPreview(null)}
          onRestore={() => restoreSnapshot(snapshotPreview.id)}
        />
      )}

      {/* Layout — form left, preview right on desktop; stacked on mobile */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,7fr)_minmax(0,8fr)] gap-6">
        <div className="space-y-8 min-w-0">
          {schema.groups.map((group) => (
            <FieldGroup
              key={group.key}
              group={group}
              draft={draft}
              onChange={update}
            />
          ))}

          {/* Section controls — homepage has full reorder; other pages get
              visibility-only toggles for their key sections. */}
          {schema.hasSectionControls && (
            <SectionControls
              visibility={getByPath(draft, "sections.visibility") || {}}
              order={resolveOrder(getByPath(draft, "sections.order"))}
              orderable={!schema.sectionTogglesOnly}
              visibleKeys={schema.visibleSectionKeys}
              onToggle={(key, val) =>
                update(`sections.visibility.${key}`, val)
              }
              onReorder={(newOrder) => update("sections.order", newOrder)}
            />
          )}
        </div>

        {/* Live preview */}
        <div className="xl:sticky xl:top-4 xl:self-start">
          <PreviewPane
            device={device}
            desktopWidth={desktopWidth}
            onDeviceChange={setDevice}
            onWidthChange={setDesktopWidth}
            previewKey={previewKey}
            previewPath={schema.previewPath}
            onRefresh={() => setPreviewKey((k) => k + 1)}
            onApply={async () => {
              await saveDraft({ silent: true });
              setPreviewKey((k) => k + 1);
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Page selector --------------------------------------------------

function PageSelector({ page, onChange, schemas }) {
  return (
    <div
      data-testid="cms-page-selector"
      className="border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-4 sm:p-5"
    >
      <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold mb-3">
        Editing page
      </p>
      <div className="flex flex-wrap gap-2">
        {schemas.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            data-testid={`cms-page-${s.key}`}
            className={`inline-flex flex-col items-start gap-0.5 px-3 py-2 border text-left transition-colors ${
              page === s.key
                ? "border-amber-400 bg-amber-400/10 text-amber-300"
                : "border-white/10 bg-black text-zinc-400 hover:border-amber-400/40 hover:text-amber-400"
            }`}
          >
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold">
              {s.label}
            </span>
            <span className="text-[10px] text-zinc-500 leading-tight">{s.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Sticky cinematic status bar ------------------------------------

function StatusBar({
  pageLabel,
  hasPendingDraft,
  publishedAt,
  publishedBy,
  saving,
  publishing,
  dirty,
  snapshotCount,
  onSave,
  onPublish,
  onDiscard,
  onToggleHistory,
  historyOpen,
}) {
  return (
    <div
      data-testid="content-editor-statusbar"
      className="sticky top-2 z-30 -mx-3 sm:mx-0 bg-gradient-to-r from-zinc-950 via-black to-zinc-950 border border-amber-500/25 relative overflow-hidden"
    >
      {/* Soft amber halo */}
      <div className="pointer-events-none absolute -top-12 -left-12 h-40 w-40 rounded-full bg-amber-400/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-12 -right-12 h-40 w-40 rounded-full bg-amber-400/[0.06] blur-3xl" />

      <div className="relative p-4 sm:p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <span
            data-testid="content-live-badge"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[10px] uppercase tracking-[0.3em] font-bold ${
              hasPendingDraft
                ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                : "border-emerald-400/60 bg-emerald-400/10 text-emerald-300"
            }`}
          >
            {hasPendingDraft ? (
              <>
                <AlertCircle className="h-3 w-3" /> Draft
              </>
            ) : (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                Live
              </>
            )}
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold truncate">
              {pageLabel} content
            </p>
            <div className="mt-0.5 text-xs text-zinc-400 leading-relaxed">
              {publishedAt ? (
                <>
                  Last published{" "}
                  <strong className="text-zinc-200">
                    {new Date(publishedAt).toLocaleString()}
                  </strong>
                  {publishedBy ? <> by {publishedBy}</> : null}
                </>
              ) : (
                "Nothing published yet — defaults are live."
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="content-history-btn"
            onClick={onToggleHistory}
            className={`inline-flex items-center gap-2 border px-3 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
              historyOpen
                ? "border-amber-400 text-amber-300 bg-amber-400/10"
                : "border-white/10 text-zinc-300 hover:border-amber-400/60"
            }`}
          >
            <History className="h-3.5 w-3.5" /> History
            {snapshotCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[9px] bg-amber-400 text-black font-bold">
                {snapshotCount}
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid="content-discard-btn"
            onClick={onDiscard}
            disabled={!hasPendingDraft}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-red-500/60 text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            <Trash2 className="h-3.5 w-3.5" /> Discard
          </button>
          <button
            type="button"
            data-testid="content-save-draft-btn"
            onClick={onSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 border border-amber-400/40 hover:bg-amber-400/10 text-amber-400 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save draft
          </button>
          <button
            type="button"
            data-testid="content-publish-btn"
            onClick={onPublish}
            disabled={publishing}
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-bold disabled:opacity-40 shadow-[0_0_30px_rgba(251,191,36,0.25)]"
          >
            {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Publish
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Snapshot history ----------------------------------------------

function SnapshotHistory({ snapshots, onPreview, onRestore, onClose }) {
  if (!snapshots.length) {
    return (
      <div
        data-testid="snapshot-history-panel"
        className="border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-5 sm:p-6"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="font-display font-black text-xl text-white tracking-tight inline-flex items-center gap-3">
            <History className="h-4 w-4 text-amber-400" /> Version history
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="snapshot-history-close"
            className="text-zinc-500 hover:text-amber-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-zinc-400">
          Nothing in history yet. Each time you publish, the previous version is captured here automatically (last 5 kept).
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="snapshot-history-panel"
      className="border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-5 sm:p-6"
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <p className="font-display font-black text-xl text-white tracking-tight inline-flex items-center gap-3">
          <History className="h-4 w-4 text-amber-400" /> Version history
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid="snapshot-history-close"
          className="text-zinc-500 hover:text-amber-400"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed">
        The last 5 published versions. Restoring a version is non-destructive — your current published state is saved first.
      </p>
      <ul className="mt-5 space-y-3">
        {snapshots.map((s) => (
          <li
            key={s.id}
            data-testid={`snapshot-row-${s.id}`}
            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-black border border-white/5 px-4 py-3"
          >
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm text-white">
                {s.published_at ? new Date(s.published_at).toLocaleString() : "Unknown time"}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {s.published_by ? `by ${s.published_by} · ` : ""}
                <span className="text-zinc-400">{s.change_summary || "No summary"}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onPreview(s.id)}
                data-testid={`snapshot-preview-${s.id}`}
                className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 text-zinc-300 hover:text-amber-400 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold"
              >
                <Eye className="h-3 w-3" /> Preview
              </button>
              <button
                type="button"
                onClick={() => onRestore(s.id)}
                data-testid={`snapshot-restore-${s.id}`}
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold"
              >
                <RotateCcw className="h-3 w-3" /> Restore
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SnapshotPreviewModal({ snapshot, onClose, onRestore }) {
  const flat = flattenForPreview(snapshot.snapshot || {});
  return (
    <div
      data-testid="snapshot-preview-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-auto bg-zinc-950 border border-amber-500/30 p-5 sm:p-7">
        <button
          type="button"
          onClick={onClose}
          data-testid="snapshot-preview-close"
          className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center text-zinc-400 hover:text-amber-400"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">
          Snapshot from {snapshot.published_at ? new Date(snapshot.published_at).toLocaleString() : "earlier"}
        </p>
        <p className="mt-1 text-sm text-zinc-400">{snapshot.change_summary}</p>
        <div className="mt-5 max-h-[55vh] overflow-auto">
          {flat.length === 0 ? (
            <p className="text-zinc-500 text-sm">No content was set in this snapshot.</p>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {flat.map(([k, v]) => (
                  <tr key={k} className="border-b border-white/5">
                    <td className="py-2 pr-4 text-amber-400/80 font-bold uppercase tracking-[0.2em] text-[10px] align-top whitespace-nowrap">{k}</td>
                    <td className="py-2 text-zinc-300 break-words">{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 border border-white/10 text-zinc-300 px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onRestore}
            data-testid="snapshot-preview-restore"
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            <RotateCcw className="h-3 w-3" /> Restore this version
          </button>
        </div>
      </div>
    </div>
  );
}

function flattenForPreview(obj, prefix = "") {
  const out = [];
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push(...flattenForPreview(v, key));
    } else {
      out.push([key, Array.isArray(v) ? v.join(", ") : v]);
    }
  }
  return out;
}

// ---------- Field group ----------------------------------------------------

function FieldGroup({ group, draft, onChange }) {
  return (
    <div
      data-testid={`content-group-${group.key}`}
      className="relative border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-5 sm:p-6 hover:border-amber-500/30 transition-colors"
    >
      <div className="pointer-events-none absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />

      <p className="font-display font-black text-xl text-white tracking-tight">
        {group.title}
      </p>
      <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{group.description}</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4 sm:gap-5">
        {group.items.map((item) => (
          <Field
            key={item.key}
            item={item}
            value={getByPath(draft, item.key)}
            onChange={(v) => onChange(item.key, v)}
          />
        ))}
      </div>
    </div>
  );
}

function Field({ item, value, onChange }) {
  const id = `field-${item.key.replace(/\./g, "-").toLowerCase()}`;
  const testid = `content-field-${item.key.replace(/\./g, "-").toLowerCase()}`;
  if (item.type === "textarea") {
    return (
      <label className="block sm:col-span-2" htmlFor={id}>
        <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
          {item.label}
        </span>
        <textarea
          id={id}
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full bg-black border border-white/10 focus:border-amber-400 focus:outline-none text-zinc-100 px-3 py-2.5 text-sm"
        />
      </label>
    );
  }
  if (item.type === "select") {
    return (
      <label className="block sm:col-span-2" htmlFor={id}>
        <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
          {item.label}
        </span>
        <select
          id={id}
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black border border-white/10 focus:border-amber-400 focus:outline-none text-zinc-100 px-3 py-2.5 text-sm"
        >
          <option value="">(Use default)</option>
          {item.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (item.type === "toggle") {
    return (
      <div className="sm:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
          {item.label}
        </p>
        <button
          type="button"
          onClick={() => onChange(!value)}
          data-testid={testid}
          aria-pressed={!!value}
          className={`inline-flex items-center gap-2 px-3 py-2 border text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
            value
              ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-300"
              : "border-white/10 bg-black text-zinc-400"
          }`}
        >
          {value ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          {value ? "On" : "Off"}
        </button>
      </div>
    );
  }
  if (item.type === "image") {
    return <ImageField item={item} value={value} onChange={onChange} id={id} testid={testid} />;
  }
  return (
    <label className="block" htmlFor={id}>
      <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
        {item.label}
      </span>
      <input
        id={id}
        type="text"
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black border border-white/10 focus:border-amber-400 focus:outline-none text-zinc-100 px-3 py-2.5 text-sm"
      />
    </label>
  );
}

function ImageField({ item, value, onChange, id, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/uploads`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (r.data?.url) {
        const absolute = r.data.url.startsWith("http")
          ? r.data.url
          : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
        onChange(absolute);
        toast.success("Image uploaded");
      } else {
        toast.error("Upload returned no URL");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewTestId = `${testid.replace("content-field-", "content-image-preview-")}`;
  return (
    <div className="sm:col-span-2 space-y-3">
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold">{item.label}</p>
      {/* Large image preview — full width, cinematic */}
      <div className="relative w-full aspect-[21/9] bg-black border border-white/10 overflow-hidden">
        {value ? (
          <img
            src={value}
            alt="preview"
            data-testid={previewTestId}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs uppercase tracking-[0.3em] text-zinc-600">
            No image set
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onPick}
          className="hidden"
          data-testid={`${testid.replace("content-field-", "content-image-file-")}`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid.replace("content-field-", "content-image-upload-")}`}
          className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] font-bold disabled:opacity-40"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? "Uploading…" : "Upload from device"}
        </button>
        <div className="flex-1 flex items-stretch border border-white/10 focus-within:border-amber-400">
          <span className="inline-flex items-center px-3 text-zinc-500 border-r border-white/10">
            <LinkIcon className="h-3.5 w-3.5" />
          </span>
          <input
            id={id}
            type="text"
            placeholder="Or paste image URL"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testid.replace("content-field-", "content-image-url-")}`}
            className="flex-1 bg-black px-3 py-2.5 text-sm focus:outline-none text-zinc-100"
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Section visibility + drag-and-drop reorder ---------------------

function SectionControls({ visibility, order, orderable = true, visibleKeys, onToggle, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(order, oldIndex, newIndex));
  };

  // For homepage: fixed = featuredEvents; ordered = midPhilosophy, pyramid, social, testimonials
  // For other pages: visibleKeys array drives the rows shown.
  const fixedSections = Object.values(MANAGED_SECTIONS).filter(
    (s) => s.position === "fixed-after-hero"
  );

  const togglesOnly = !orderable && Array.isArray(visibleKeys);
  const togglesOnlyRows = togglesOnly
    ? visibleKeys.map((k) => ({
        key: k,
        label: k.charAt(0).toUpperCase() + k.slice(1),
        description: `Show/hide the ${k} section on this page.`,
      }))
    : [];

  return (
    <div
      data-testid="content-group-sections"
      className="relative border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-5 sm:p-6"
    >
      <div className="pointer-events-none absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
      <p className="font-display font-black text-xl text-white tracking-tight">
        Sections
      </p>
      <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
        {orderable
          ? "Toggle visibility per section. The cinematic late-stage block can also be reordered via drag-and-drop (works on touch + keyboard)."
          : "Toggle visibility for the key sections on this page."}
      </p>

      {/* Toggles-only mode for Phase 3 pages */}
      {togglesOnly && (
        <div className="mt-6 space-y-3">
          {togglesOnlyRows.map((row) => (
            <VisibilityRow
              key={row.key}
              section={row}
              visible={isVisible(visibility, row.key)}
              onToggle={(v) => onToggle(row.key, v)}
            />
          ))}
        </div>
      )}

      {/* Homepage: fixed sections */}
      {orderable && fixedSections.length > 0 && (
        <div className="mt-6 space-y-3">
          {fixedSections.map((s) => (
            <VisibilityRow
              key={s.key}
              section={s}
              visible={isVisible(visibility, s.key)}
              onToggle={(v) => onToggle(s.key, v)}
            />
          ))}
        </div>
      )}

      {/* Homepage: ordered draggable sections */}
      {orderable && (
        <>
          <p className="mt-7 text-[10px] uppercase tracking-[0.3em] text-amber-400/80 font-bold">
            Cinematic block — drag to reorder
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <ul className="mt-3 space-y-3" data-testid="sections-sortable-list">
                {order.map((key) => (
                  <SortableSectionRow
                    key={key}
                    id={key}
                    section={MANAGED_SECTIONS[key]}
                    visible={isVisible(visibility, key)}
                    onToggle={(v) => onToggle(key, v)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}

function VisibilityRow({ section, visible, onToggle }) {
  return (
    <div
      data-testid={`section-row-${section.key}`}
      className="flex items-start justify-between gap-4 bg-black border border-white/5 px-4 py-3"
    >
      <div>
        <p className="font-display font-bold text-sm text-white">{section.label}</p>
        <p className="text-xs text-zinc-500 leading-relaxed mt-0.5">{section.description}</p>
      </div>
      <VisibilityToggle
        testId={`section-toggle-${section.key}`}
        value={visible}
        onChange={onToggle}
      />
    </div>
  );
}

function SortableSectionRow({ id, section, visible, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  if (!section) return null;
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`section-row-${id}`}
      className="flex items-center gap-3 bg-black border border-white/5 px-2 sm:px-3 py-3 select-none touch-none"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`section-handle-${id}`}
        aria-label={`Drag ${section.label}`}
        className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-amber-400 transition-colors px-2 py-2"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-sm text-white truncate">{section.label}</p>
        <p className="text-xs text-zinc-500 leading-relaxed truncate">{section.description}</p>
      </div>
      <VisibilityToggle
        testId={`section-toggle-${id}`}
        value={visible}
        onChange={onToggle}
      />
    </li>
  );
}

function VisibilityToggle({ testId, value, onChange }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 border text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${
        value
          ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10"
          : "border-zinc-700 text-zinc-500 bg-black"
      }`}
    >
      {value ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      {value ? "On" : "Off"}
    </button>
  );
}

// ---------- Live preview ---------------------------------------------------

const DESKTOP_WIDTHS = [1280, 1440, 1920];

function PreviewPane({ device, desktopWidth, onDeviceChange, onWidthChange, previewKey, previewPath = "/", onRefresh, onApply }) {
  const url = `${previewPath}${previewPath.includes("?") ? "&" : "?"}preview=1`;

  return (
    <div
      data-testid="content-preview-pane"
      className="border border-amber-500/15 bg-gradient-to-b from-zinc-950 to-black p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 border border-white/10 p-0.5">
          <DeviceBtn
            label="Desktop"
            Icon={Monitor}
            active={device === "desktop"}
            onClick={() => onDeviceChange("desktop")}
            testId="preview-device-desktop"
          />
          <DeviceBtn
            label="Mobile"
            Icon={Smartphone}
            active={device === "mobile"}
            onClick={() => onDeviceChange("mobile")}
            testId="preview-device-mobile"
          />
        </div>

        {device === "desktop" && (
          <div className="flex items-center gap-1 border border-white/10 p-0.5">
            {DESKTOP_WIDTHS.map((w) => (
              <button
                key={w}
                type="button"
                data-testid={`preview-width-${w}`}
                onClick={() => onWidthChange(w)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${
                  desktopWidth === w ? "bg-amber-400 text-black" : "text-zinc-400 hover:text-amber-400"
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={onApply}
            data-testid="preview-apply-btn"
            className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold"
            title="Save draft and refresh preview"
          >
            <Save className="h-3 w-3" /> Apply
          </button>
          <button
            type="button"
            onClick={onRefresh}
            data-testid="preview-refresh-btn"
            className="inline-flex items-center gap-2 border border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Frame */}
      <div className="mt-4 bg-black border border-white/10 p-3 sm:p-5 overflow-auto">
        <DevicePreviewFrame
          device={device}
          desktopWidth={desktopWidth}
          previewKey={previewKey}
          url={url}
        />
      </div>

      <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        Preview shows the <span className="text-amber-400 font-bold">PUBLISHED</span> site. Hit <em>Publish</em> to make your draft live.
      </p>
    </div>
  );
}

function DeviceBtn({ label, Icon, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${
        active ? "bg-amber-400 text-black" : "text-zinc-400 hover:text-amber-400"
      }`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function DevicePreviewFrame({ device, desktopWidth, previewKey, url }) {
  // Always render the iframe at its natural width and scale it down with CSS
  // so the user sees the real layout without cramming it into a 600px column.
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const naturalWidth = device === "mobile" ? 390 : desktopWidth;
  const naturalHeight = device === "mobile" ? 844 : 900;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const compute = () => {
      const available = el.clientWidth;
      setScale(Math.min(1, available / naturalWidth));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [naturalWidth]);

  return (
    <div
      ref={containerRef}
      data-testid={`preview-frame-${device}`}
      className="w-full mx-auto flex items-start justify-center"
      style={{ height: naturalHeight * scale }}
    >
      <iframe
        key={previewKey}
        src={url}
        title="Homepage preview"
        style={{
          width: naturalWidth,
          height: naturalHeight,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      />
    </div>
  );
}
